import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Phone, MapPin, Clock, RefreshCw, AlertTriangle, User, UserCog,
  HandHeart, PackageCheck, CheckCircle2, Pencil,
  Bell, BellOff, X,
} from "lucide-react";
import { api, formatApiError, formatDate, formatDateTime, formatRelativeFromNow, formatClockTime, todayISODate } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { StatusBadge } from "@/components/StatusBadge";
import NewOrderModal from "@/components/NewOrderModal";
import EditOrderModal from "@/components/EditOrderModal";
import { OrderLines } from "@/components/OrderLines";
import { HandoffHistory } from "@/components/HandoffHistory";
import { OrderPaperPhoto } from "@/components/OrderPaperPhoto";
import { DeliveryPhotoButton } from "@/components/DeliveryPhotoButton";
import { useAlarm } from "@/hooks/useAlarm";
import { PushNotificationButton } from "@/components/PushNotificationButton";

const TABS = [
  { key: "bekliyor",       label: "Bekleyenler" },
  { key: "ileri_tarihli",  label: "İleri Tarihli" },
  { key: "teslim_alindi",  label: "Teslim Alınanlar" },
  { key: "teslim_edildi",  label: "Teslim Edilenler" },
];

function isFutureDate(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const t = new Date();
  // bugünün gece yarısı (00:00) — bu noktadan sonrası "ileri tarihli"
  const tomorrow = new Date(t.getFullYear(), t.getMonth(), t.getDate() + 1);
  return d.getTime() >= tomorrow.getTime();
}

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("bekliyor");
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [preview, setPreview] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [mute, setMute] = useState(false);
  // "Teslim Edilenler" tab'ı için tek-gün tarih filtresi (varsayılan: bugün, ISO yyyy-mm-dd).
  const [deliveredDate, setDeliveredDate] = useState(todayISODate());
  const [, tick] = useState(0);

  const fetchAll = async () => {
    try {
      // Compute a half-open local-day window for the delivered filter and
      // pass it to the backend so it can prune the response to just active
      // orders + that day's delivered ones. Frontend already filters by day
      // for display; sending the range upstream keeps payload predictable
      // regardless of how many historical delivered orders live in the DB.
      const startLocal = new Date(`${deliveredDate}T00:00:00`);
      const endLocal   = new Date(startLocal.getTime() + 24 * 60 * 60 * 1000);
      const params = new URLSearchParams({
        delivered_from: startLocal.toISOString(),
        delivered_to:   endLocal.toISOString(),
      });
      const { data } = await api.get(`/orders?${params.toString()}`);
      setOrders(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Sekme görünür olduğunda fetch et + her 15 saniyede bir yenile.
  // Sekme arka plana atıldığında polling durur (mobil veri tasarrufu + sunucuya gereksiz yük yok).
  // deliveredDate değişince de otomatik yeniden çeker (kullanıcı gün seçince).
  useEffect(() => {
    let timer;
    const start = () => {
      fetchAll();
      timer = setInterval(fetchAll, 15000);
    };
    const stop = () => { if (timer) { clearInterval(timer); timer = undefined; } };
    const onVis = () => {
      if (document.visibilityState === "visible") {
        stop();
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveredDate]);

  useEffect(() => {
    const t = setInterval(() => tick((v) => v + 1), 20000);
    return () => clearInterval(t);
  }, []);

  const groups = useMemo(() => {
    const bekliyorAll = orders.filter((o) => o.status === "bekliyor");
    // Teslim edilenleri YALNIZ seçili gün için listele (varsayılan: bugün).
    // Bu sayede aylar/yıllar sonra binlerce kayıt birikse de ekran hızlı kalır.
    // En son teslim edilen üstte (delivered_at desc); ts yoksa delivery_time'a düş.
    const deliveredAll = orders.filter((o) => o.status === "teslim_edildi");
    const deliveredFiltered = deliveredAll.filter((o) => {
      const ts = o.delivered_at || o.delivery_time;
      if (!ts) return false;
      try {
        const d = new Date(ts);
        const tz = d.getTimezoneOffset() * 60000;
        const local = new Date(d.getTime() - tz).toISOString().slice(0, 10);
        return local === deliveredDate;
      } catch { return false; }
    }).sort((a, b) => {
      const ta = new Date(a.delivered_at || a.delivery_time).getTime();
      const tb = new Date(b.delivered_at || b.delivery_time).getTime();
      return tb - ta; // en yeni teslim üstte
    });
    return {
      bekliyor:      bekliyorAll.filter((o) => !isFutureDate(o.delivery_time)),
      ileri_tarihli: bekliyorAll
        .filter((o) => isFutureDate(o.delivery_time))
        .sort((a, b) => new Date(a.delivery_time) - new Date(b.delivery_time)),
      teslim_alindi: orders.filter((o) => o.status === "teslim_alindi"),
      teslim_edildi: deliveredFiltered,
    };
  }, [orders, deliveredDate]);

  const visible = groups[tab] || [];
  const hasWarning = useMemo(
    () => (groups.bekliyor || []).some((o) => o.warning),
    [groups],
  );
  useAlarm(hasWarning && !mute);

  return (
    <div className="min-h-screen bg-zinc-50">
      <AppHeader
        title="Siparişler"
        subtitle="Yönetici Paneli"
        right={
          <>
            <button
              data-testid="toggle-mute"
              onClick={() => setMute((m) => !m)}
              title={mute ? "Sesi aç" : "Sesi kapat"}
              className={`hidden sm:inline-flex items-center gap-1.5 px-3 py-2 border-2 transition-colors text-xs font-bold uppercase tracking-wider ${
                mute ? "bg-zinc-100 text-zinc-500 border-zinc-300" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-950"
              }`}
            >
              {mute ? <BellOff size={14} strokeWidth={2.5} /> : <Bell size={14} strokeWidth={2.5} />}
              {mute ? "Sessiz" : "Sesli"}
            </button>
            <button
              data-testid="refresh-button"
              onClick={fetchAll}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 border-2 border-zinc-200 hover:border-zinc-950 transition-colors text-xs font-bold uppercase tracking-wider"
            >
              <RefreshCw size={14} strokeWidth={2.5} /> Yenile
            </button>
            <PushNotificationButton />
            <button
              data-testid="new-order-button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-950 text-white text-xs font-bold uppercase tracking-wider hover:bg-zinc-800 transition-colors"
            >
              <Plus size={14} strokeWidth={2.5} /> Yeni Sipariş
            </button>
          </>
        }
      />

      <main className="app-safe-bottom max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-5 flex gap-2 overflow-x-auto no-scrollbar">
          {TABS.map((t) => {
            const count = groups[t.key]?.length || 0;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`shrink-0 px-4 py-2 text-xs font-bold uppercase tracking-wider border-2 transition-colors ${
                  active ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-950"
                }`}
              >
                {t.label}{" "}
                <span className={`font-mono ${active ? "text-white/80" : "text-zinc-500"}`}>({count})</span>
              </button>
            );
          })}
        </div>

        {tab === "teslim_edildi" && (
          <div className="mb-4 bg-white border-2 border-zinc-200 p-3 flex items-center gap-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 shrink-0">
              Gün
            </div>
            <input
              data-testid="delivered-date-input"
              type="date"
              value={deliveredDate}
              max={todayISODate()}
              onChange={(e) => setDeliveredDate(e.target.value)}
              className="flex-1 min-w-0 brutalist-input !py-1.5 !text-sm font-bold tracking-tight text-zinc-950"
            />
            <button
              type="button"
              data-testid="delivered-today-btn"
              onClick={() => setDeliveredDate(todayISODate())}
              className="shrink-0 px-3 py-1.5 border-2 border-zinc-300 hover:border-zinc-950 text-xs font-bold uppercase tracking-wider"
            >
              Bugün
            </button>
          </div>
        )}

        {hasWarning && tab === "bekliyor" && (
          <div
            data-testid="warning-banner"
            className="mb-4 p-4 bg-red-600 text-white border-2 border-red-700 brutal-shadow flex items-center gap-3 animate-pulse"
          >
            <AlertTriangle size={22} strokeWidth={2.5} />
            <div className="flex-1">
              <div className="font-black uppercase tracking-wider text-sm">
                Bekleyen siparişler arasında geciken var!
              </div>
              <div className="text-xs text-red-100 mt-0.5">
                Teslim saatine 30 dakikadan az kaldı veya geçti — bir kuryenin teslim alması bekleniyor.
              </div>
            </div>
            <button
              data-testid="dismiss-alarm"
              onClick={() => setMute(true)}
              className="px-3 py-1.5 bg-white text-red-700 text-xs font-bold uppercase tracking-wider hover:bg-red-50"
            >
              Sesi Kapat
            </button>
          </div>
        )}

        {loading ? (
          <Empty text="Yükleniyor…" />
        ) : visible.length === 0 ? (
          <Empty text={
            tab === "bekliyor"      ? "Bugün için bekleyen sipariş yok." :
            tab === "ileri_tarihli" ? "İleri tarihli sipariş yok." :
            tab === "teslim_alindi" ? "Teslim alınmış sipariş yok." :
                                      (deliveredDate === todayISODate()
                                        ? "Bugün için teslim edilen sipariş yok."
                                        : `${new Date(deliveredDate).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })} tarihinde teslim edilen sipariş yok.`)
          } />
        ) : (
          <ul className="space-y-3" data-testid="orders-list">
            {visible.map((o, i) => (
              <OrderRow key={o.id} order={o} index={i} onPreview={(src) => setPreview(src)} onOpenHistory={setHistoryFor} onEdit={setEditTarget} />
            ))}
          </ul>
        )}
      </main>

      <NewOrderModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(o) => {
          setOrders((prev) => [o, ...prev]);
          // Yeni siparişi gizleyen bir tab'da değilsek, otomatik o tab'a geç.
          // Bu sayede oluşturulan sipariş ekstra tıklama gerektirmeden görünür.
          const now = new Date();
          const dt  = new Date(o.delivery_time);
          const sameDay = dt.toDateString() === now.toDateString();
          if (o.status === "bekliyor" && sameDay) setTab("bekliyor");
          else if (o.status === "bekliyor" && !sameDay) setTab("ileri_tarihli");
          else setTab(o.status);
          // Yeni sipariş kartına yumuşak scroll
          setTimeout(() => {
            const el = document.querySelector(`[data-testid="order-row-${o.id}"]`);
            el?.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 200);
        }}
      />

      <EditOrderModal
        order={editTarget}
        onClose={() => setEditTarget(null)}
        onUpdated={(o) => setOrders((prev) => prev.map((p) => p.id === o.id ? o : p))}
        onDeleted={(id) => setOrders((prev) => prev.filter((p) => p.id !== id))}
      />

      {preview && (
        <div
          data-testid="image-preview-modal"
          className="fixed inset-0 z-50 bg-zinc-950/85 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
        >
          <button onClick={() => setPreview(null)} className="absolute top-4 right-4 text-white">
            <X size={28} />
          </button>
          <img src={preview} alt="Önizleme" className="max-h-[85vh] max-w-full object-contain border-4 border-white" />
        </div>
      )}

      {historyFor && (
        <CustomerHistoryModal order={historyFor} onClose={() => setHistoryFor(null)} />
      )}
    </div>
  );
}

function CustomerHistoryModal({ order, onClose }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data: c } = await api.get(`/customers/by-phone/${order.phone}`);
        const { data: orders } = await api.get(`/customers/${c.id}/orders`);
        setHistory({ customer: c, orders });
      } catch (e) {
        toast.error(formatApiError(e));
        onClose();
      } finally { setLoading(false); }
    })();
  }, [order, onClose]);
  return (
    <div
      data-testid="customer-history-modal"
      className="fixed inset-0 z-50 bg-zinc-950/70 flex items-start sm:items-center justify-center p-0 sm:p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div className="bg-white border-2 border-zinc-950 brutal-shadow-dark w-full sm:max-w-2xl my-0 sm:my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b-2 border-zinc-200 px-5 py-3">
          <h2 className="text-lg font-black tracking-tight text-zinc-950">Müşteri Geçmişi</h2>
          <button onClick={onClose} className="p-2 text-zinc-500 hover:text-zinc-950"><X size={18} /></button>
        </div>
        {loading || !history ? (
          <div className="p-10 text-center font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Yükleniyor…</div>
        ) : (
          <>
            <div className="p-5 grid grid-cols-2 sm:grid-cols-4 gap-3 border-b-2 border-zinc-200">
              <Mini label="Müşteri" value={history.customer.full_name} testId="hist-name" />
              <Mini label="Telefon" value={<a href={`tel:${history.customer.phone}`} className="hover:underline">{history.customer.phone}</a>} testId="hist-phone" />
              <Mini label="Toplam Sipariş" value={history.customer.total_orders} testId="hist-count" />
              <Mini label="Toplam Harcama" value={`₺${history.customer.total_amount.toFixed(2)}`} testId="hist-spent" />
              <Mini label="Ortalama" value={`₺${history.customer.avg_amount.toFixed(2)}`} testId="hist-avg" />
              <Mini label="İlk Sipariş" value={formatDateTime(history.customer.first_order_at)} testId="hist-first" />
              <Mini label="Son Sipariş" value={formatDateTime(history.customer.last_order_at)} testId="hist-last" />
              <Mini label="Adres" value={<span className="text-xs">{history.customer.address}</span>} testId="hist-address" />
            </div>
            <ul className="max-h-[50vh] overflow-y-auto" data-testid="customer-history-list">
              {history.orders.length === 0 ? (
                <li className="p-6 text-center font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Sipariş yok</li>
              ) : (
                history.orders.map((o) => (
                  <li key={o.id} data-testid={`history-order-${o.id}`} className="px-5 py-3 border-b border-zinc-100 last:border-b-0 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-bold text-zinc-950 text-sm">{formatDateTime(o.delivery_time)}</div>
                      <div className="text-xs text-zinc-600 break-words">{summarizeOrder(o)}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <StatusBadge status={o.status} />
                      {o.amount > 0 && <div className="font-mono text-[10px] text-zinc-700 mt-1">₺{o.amount.toFixed(2)}</div>}
                    </div>
                  </li>
                ))
              )}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

function summarizeOrder(o) {
  if (Array.isArray(o.items) && o.items.length) {
    return o.items.join(" • ");
  }
  if (Array.isArray(o.notes) && o.notes.length) {
    return o.notes.join(" • ");
  }
  return o.description || "—";
}

function Mini({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="text-sm font-bold text-zinc-950 mt-0.5 truncate">{value}</div>
    </div>
  );
}

function OrderRow({ order: o, index, onPreview, onOpenHistory, onEdit }) {
  const warning = o.warning && o.status !== "teslim_edildi";
  const isLate = o.is_late && o.status !== "teslim_edildi";
  const canEdit = o.status !== "teslim_edildi";
  const borderColor = isLate ? "border-red-500" : warning ? "border-amber-400" : "border-zinc-200";
  const accentBg    = isLate ? "bg-red-50" : warning ? "bg-amber-50" : "bg-white";
  const timeText    = isLate ? "text-red-700" : warning ? "text-amber-700" : "text-zinc-950";
  const timeIcon    = isLate ? "text-red-600" : warning ? "text-amber-500" : "text-zinc-500";
  const timeSub     = isLate ? "text-red-600" : warning ? "text-amber-700" : "text-zinc-500";

  return (
    <li
      style={{ animationDelay: `${index * 25}ms` }}
      className={`slide-up border-2 brutal-shadow ${accentBg} ${borderColor}`}
      data-testid={`order-row-${o.id}`}
    >
      <div className="p-4 sm:p-5 flex flex-col sm:flex-row gap-4">
        <OrderPaperPhoto
          orderId={o.id}
          hasImage={!!o.has_image}
          alt={o.full_name}
          onPreview={onPreview}
          testIdPrefix={`order-${o.id}`}
          size="lg"
        />

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <StatusBadge status={o.status} warning={warning} testId={`order-status-${o.id}`} />
            {isLate && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest" data-testid={`order-late-${o.id}`}>
                GECİKTİ
              </span>
            )}
            {!isLate && warning && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-400 text-zinc-950 text-[10px] font-bold uppercase tracking-widest" data-testid={`order-warning-${o.id}`}>
                30 DK
              </span>
            )}
          </div>

          {/* TOP ROW: [Customer Name] ............ [Time]
              BOTTOM ROW: [Date] ................. [Remaining]
              Müşteri adı: tek satır, fluid font (clamp), asla "..." kullanmaz.
              Adın taşma riski olduğu çok uzun isimlerde otomatik küçülür. */}
          <div className="mb-2 space-y-1">
            <div className="flex items-baseline justify-between gap-2">
              <button
                type="button"
                data-testid={`customer-history-${o.id}`}
                onClick={() => onOpenHistory?.(o)}
                className="font-black text-zinc-950 hover:underline text-left flex-1 min-w-0 leading-tight tracking-tight whitespace-nowrap overflow-hidden"
                style={{ fontSize: "clamp(0.6875rem, 4vw, 1.875rem)" }}
              >
                {o.full_name}
              </button>
              <div
                data-testid={`order-${o.id}-delivery-time`}
                className={`shrink-0 font-black tabular-nums tracking-tight leading-none whitespace-nowrap flex items-center gap-1 ${timeText}`}
                style={{ fontSize: "clamp(0.75rem, 3.4vw, 1.25rem)" }}
              >
                <Clock size={14} strokeWidth={2.5} className={timeIcon} />
                {formatClockTime(o.delivery_time)}
              </div>
            </div>
            <div className="flex items-baseline justify-between gap-2 font-mono text-[10px] uppercase tracking-[0.15em]">
              <span className="text-zinc-500 whitespace-nowrap">
                {formatDate(o.delivery_time)}
              </span>
              <span className={`whitespace-nowrap ${timeSub}`}>
                {formatRelativeFromNow(o.delivery_time)}
              </span>
            </div>
          </div>

          <div className="space-y-1 text-sm text-zinc-700">
            <a href={`tel:${o.phone}`} className="flex items-center gap-2 hover:underline">
              <Phone size={13} className="text-zinc-400" strokeWidth={2.5} />
              {o.phone}
            </a>
            <div className="flex items-start gap-2">
              <MapPin size={13} className="text-zinc-400 mt-0.5 shrink-0" strokeWidth={2.5} />
              <span className="line-clamp-2">{o.address}</span>
            </div>
          </div>

          <OrderLines order={o} testIdPrefix={`order-${o.id}`} dense />

          {/* Responsibility chain */}
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Resp
              testId={`resp-creator-${o.id}`}
              icon={<UserCog size={11} strokeWidth={2.5} />}
              label="Siparişi İşleyen"
              who={o.created_by_name}
              at={o.created_at}
              color="zinc"
            />
            <Resp
              testId={`resp-claim-${o.id}`}
              icon={<HandHeart size={11} strokeWidth={2.5} />}
              label="Götürecek Kişi"
              who={o.claimed_by_name}
              at={o.claimed_at}
              color="amber"
            />
            <Resp
              testId={`resp-pickup-${o.id}`}
              icon={<PackageCheck size={11} strokeWidth={2.5} />}
              label="Teslim Alan"
              who={o.picked_up_by_name}
              at={o.picked_up_at}
              color="orange"
            />
            <Resp
              testId={`resp-deliver-${o.id}`}
              icon={<CheckCircle2 size={11} strokeWidth={2.5} />}
              label="Teslim Eden"
              who={o.delivered_at ? o.picked_up_by_name : null}
              at={o.delivered_at}
              color="emerald"
            />
          </div>

          <div className="mt-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
            Açılış {formatClockTime(o.created_at)}
          </div>

          <HandoffHistory history={o.handoff_history} testIdPrefix={`order-${o.id}`} />

          <DeliveryPhotoButton
            orderId={o.id}
            hasDeliveryImage={!!o.has_delivery_image}
            onPreview={onPreview}
            testId={`delivery-photo-${o.id}`}
          />

          {o.updated_by_name && (
            <div
              data-testid={`order-${o.id}-updated`}
              className="mt-3 flex items-center gap-2 px-2.5 py-1.5 border border-amber-200 bg-amber-50 font-mono text-[10px] uppercase tracking-wider text-amber-800"
            >
              <Pencil size={10} strokeWidth={2.5} />
              <span>Güncelleyen: <b>{o.updated_by_name}</b></span>
              <span className="text-amber-400">·</span>
              <span>{formatClockTime(o.updated_at)}</span>
            </div>
          )}

          {canEdit && onEdit && (
            <button
              type="button"
              data-testid={`edit-order-${o.id}`}
              onClick={() => onEdit(o)}
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 border-2 border-amber-500 text-amber-700 text-[11px] font-bold uppercase tracking-widest hover:bg-amber-50 transition-colors"
            >
              <Pencil size={11} strokeWidth={2.5} />
              Siparişi Güncelle
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

function Resp({ icon, label, who, at, color, testId }) {
  const filled = !!who;
  const palette = {
    zinc:    { bg: "bg-zinc-100",   border: "border-zinc-300",    text: "text-zinc-900" },
    amber:   { bg: "bg-amber-50",   border: "border-amber-300",   text: "text-amber-900" },
    orange:  { bg: "bg-orange-50",  border: "border-orange-300",  text: "text-orange-900" },
    emerald: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-900" },
  }[color] || { bg: "bg-zinc-50", border: "border-zinc-200", text: "text-zinc-700" };

  return (
    <div
      data-testid={testId}
      className={`px-2.5 py-1.5 border ${filled ? `${palette.bg} ${palette.border}` : "bg-zinc-50 border-zinc-200"}`}
    >
      <div className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
        {icon}
        {label}
      </div>
      <div className={`text-sm font-bold mt-0.5 break-words ${filled ? palette.text : "text-zinc-400"}`}>
        {who || "—"}
      </div>
      {at && (
        <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
          {formatClockTime(at)}
        </div>
      )}
    </div>
  );
}

function Empty({ text }) {
  return (
    <div className="border-2 border-dashed border-zinc-200 bg-white p-10 text-center">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">{text}</div>
    </div>
  );
}
